/**
 * Impresión de un PDF descargado con sesión.
 *
 * Imprimir el PDF dentro de un iframe oculto depende del visor del navegador (Chrome, Safari y Edge lo
 * ignoran o lo bloquean según el tamaño del marco y la política de seguridad de la página). Para que
 * funcione igual en todos, las páginas se dibujan con pdf.js como imágenes y se imprimen desde un
 * documento HTML propio, que todos los navegadores saben imprimir.
 */

/** Resolución del dibujo: ~200 dpi alcanza para texto nítido y códigos QR legibles. */
const SCALE = 200 / 72

interface PrintPage {
  url: string
  /** Tamaño de la página en puntos (1/72 de pulgada). */
  width: number
  height: number
}

async function renderPages(blob: Blob): Promise<PrintPage[]> {
  const pdfjs = await import('pdfjs-dist')
  const { default: workerSrc } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

  // Sin FontFace: la página tiene una política de seguridad estricta y solo se necesita un dibujo.
  const task = pdfjs.getDocument({
    data: new Uint8Array(await blob.arrayBuffer()),
    disableFontFace: true,
  })
  const pdf = await task.promise
  const pages: PrintPage[] = []
  try {
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n)
      const base = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: SCALE })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const canvasContext = canvas.getContext('2d')
      if (!canvasContext) throw new Error('El navegador no pudo preparar la impresión')
      await page.render({ canvas, canvasContext, viewport }).promise
      const image = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!image) throw new Error('El navegador no pudo preparar la impresión')
      pages.push({ url: URL.createObjectURL(image), width: base.width, height: base.height })
      canvas.width = canvas.height = 0
    }
  } finally {
    await task.destroy()
  }
  return pages
}

function printHtml(pages: PrintPage[]) {
  const { width, height } = pages[0] as PrintPage
  const images = pages.map((p) => `<img src="${p.url}" alt="">`).join('')
  return `<!doctype html><html><head><meta charset="utf-8"><title>Imprimir</title><style>
@page{size:${width}pt ${height}pt;margin:0}
html,body{margin:0;padding:0;background:#fff}
img{display:block;width:${width}pt;height:${height}pt;break-after:page}
img:last-child{break-after:auto}
</style></head><body>${images}</body></html>`
}

/** Dibuja el PDF y abre el diálogo de impresión del navegador (sin dejar pestañas abiertas). */
export async function printPdfBlob(blob: Blob) {
  const pages = await renderPages(blob)
  if (!pages.length) throw new Error('El comprobante no tiene páginas para imprimir')

  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.tabIndex = -1
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none'
  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    frame.remove()
    for (const p of pages) URL.revokeObjectURL(p.url)
  }

  await new Promise<void>((resolve, reject) => {
    frame.onload = async () => {
      try {
        const win = frame.contentWindow
        if (!win) throw new Error('El navegador no pudo abrir la impresión')
        // Las imágenes deben estar listas o el diálogo abre con la vista previa en blanco.
        await Promise.all([...win.document.images].map((img) => img.decode().catch(() => undefined)))
        win.addEventListener('afterprint', cleanup)
        win.focus()
        win.print()
        // Respaldo: algunos navegadores no avisan al cerrar el diálogo.
        setTimeout(cleanup, 5 * 60_000)
        resolve()
      } catch (error) {
        cleanup()
        reject(error)
      }
    }
    frame.srcdoc = printHtml(pages)
    document.body.appendChild(frame)
  })
}
